/* eslint-disable no-unused-vars -- The legacy base ESLint rule misreads TypeScript function signature parameters. */
import { auth } from './firebaseAuth';

export type PushSettings = {
  available: boolean; appId?: string; safariWebId?: string; externalId?: string; enabled: boolean;
  engagementAvailable?: boolean; engagementEnabled?: boolean;
};
export type PushSDK = {
  init: (options: Record<string, unknown>) => Promise<void>;
  login: (id: string) => Promise<void>;
  logout: () => Promise<void>;
  Notifications: {
    permission: boolean;
    requestPermission: () => Promise<void>;
    addEventListener: (event: string, listener: () => void) => void;
    removeEventListener: (event: string, listener: () => void) => void;
  };
  User: { PushSubscription: {
    optedIn: boolean;
    optIn: () => Promise<void>;
    optOut: () => Promise<void>;
    addEventListener: (event: string, listener: () => void) => void;
    removeEventListener: (event: string, listener: () => void) => void;
  } };
};

let sdkPromise: Promise<PushSDK> | undefined;
let sdkInstance: PushSDK | undefined;
let operation: Promise<unknown> = Promise.resolve();

export function serializePush<T>(callback: () => Promise<T>): Promise<T> {
  const next = operation.catch(() => undefined).then(callback);
  operation = next;
  return next;
}

export function pushSupport() {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return {
    needsInstall: ios && !standalone,
    supported: window.isSecureContext && 'serviceWorker' in navigator &&
      'PushManager' in window && 'Notification' in window,
  };
}

export async function pushRequest(action?: 'prepare' | 'preference' | 'engagement', enabled?: boolean): Promise<PushSettings> {
  const user = auth.currentUser;
  if (!user) throw new Error('Bildirim ayarları için giriş yapmalısın.');
  const token = await user.getIdToken();
  const response = await fetch('/api/push/preferences', {
    method: action ? 'POST' : 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...(action ? { body: JSON.stringify({ action, enabled }) } : {}),
  });
  if (!response.ok) throw new Error('Bildirim ayarları kaydedilemedi. Lütfen tekrar dene.');
  const data = await response.json();
  if (auth.currentUser?.uid !== user.uid) throw new Error('Oturum değişti. Lütfen tekrar dene.');
  return data;
}

export function loadPushSDK(appId: string, safariWebId?: string): Promise<PushSDK> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<PushSDK>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Bildirim servisine ulaşılamadı. Sayfayı yenileyip tekrar dene.')), 15000);
    const win = window as typeof window & { OneSignalDeferred?: Array<(sdk: PushSDK) => void> };
    win.OneSignalDeferred = win.OneSignalDeferred || [];
    win.OneSignalDeferred.push(async sdk => {
      try {
        await sdk.init({
          appId,
          ...(safariWebId ? { safari_web_id: safariWebId } : {}),
          serviceWorkerPath: 'push/OneSignalSDKWorker.js',
          serviceWorkerParam: { scope: '/push/' },
          notifyButton: { enable: false },
          promptOptions: { slidedown: { prompts: [] } },
          welcomeNotification: { disable: true },
          allowLocalhostAsSecureOrigin: process.env.NODE_ENV !== 'production',
        });
        sdkInstance = sdk;
        clearTimeout(timer);
        resolve(sdk);
      } catch {
        clearTimeout(timer);
        reject(new Error('Bildirim servisi başlatılamadı. Lütfen tekrar dene.'));
      }
    });
    const script = document.createElement('script');
    script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
    script.defer = true;
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Bildirim servisi yüklenemedi. Bağlantını ve içerik engelleyicini kontrol et.'));
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
}

export async function disconnectPushDevice(): Promise<void> {
  if (!sdkInstance) return;
  await sdkInstance.User.PushSubscription.optOut();
  await sdkInstance.logout();
}

export async function signOutWithPush(): Promise<void> {
  // Do not silently leave a shared browser attached to the previous account.
  await serializePush(disconnectPushDevice);
  await auth.signOut();
}
